import { getRiskSummariesByIds } from "../api/api-methods";
import {
  extractRiskRowIds,
  getRowRiskSummary,
  withoutRiskSummaryColumn,
} from "./riskSummaryGridUtils";

// Grid exports must contain every record matching the current filters, not just
// the server page on screen (the grids fetch page_size 21, so a snapshot export
// capped at 21 rows). The builder pulls all pages through the caller's
// *AllPages fetcher, runs the grid's own client filter/sort pipeline, and maps
// rows through the grid's export record mapper.
// prepareContext (optional, async) runs once over the refined set and its
// result is handed to every mapRecord call. Used to batch-fetch per-row data
// for the FULL export (e.g. published Risk Summaries), which the page-scoped
// grid state cannot provide.
export const buildExportDataFromAllPages = async ({
  fetchAllPages,
  refineItems,
  prepareContext,
  mapRecord,
  headers,
}) => {
  const result = await fetchAllPages();
  const items = Array.isArray(result) ? result : (result?.items ?? []);
  const refined = refineItems ? refineItems(items) : items;
  const context = prepareContext ? await prepareContext(refined) : undefined;
  return {
    records: refined.map((item) => mapRecord(item, context)),
    headers: [...headers],
  };
};

// Shared shape of the three RFI grids' exports: all pages, then the published
// Risk Summaries batch-fetched for the full export set, then the grid's own
// mapper called with (item, summaryOrNull). Keeps the per-grid wiring down to
// what actually differs: the fetcher, the refine pipeline, mapper and headers.
// Each row resolves its summary exactly like the grid cell does, and a viewer who
// cannot see the Risk Summary column gets neither the column nor the lookup.
export const buildRiskAwareExportData = ({
  fetchAllPages,
  refineItems,
  mapRecordWithSummary,
  headers,
  includeRisk = true,
}) =>
  buildExportDataFromAllPages({
    fetchAllPages,
    refineItems,
    prepareContext: includeRisk
      ? (rows) => getRiskSummariesByIds(extractRiskRowIds(rows))
      : undefined,
    mapRecord: (item, summaries) =>
      mapRecordWithSummary(item, includeRisk ? getRowRiskSummary(item, summaries) : null),
    headers: includeRisk ? headers : withoutRiskSummaryColumn(headers),
  });
